import { Container, Sprite, type Texture } from 'pixi.js';
import { bulbTexture, glowTexture, prefersReducedMotion, REDUCED_STEP_MS } from './glow';

/** `ready`: lit from the bottom as the Ready meter fills. `chase`: a band
 *  runs up both sides while a Lamp Run is on. `flash`: everything blinks
 *  gold for a win, then goes back. */
export type SideLightsMode = 'ready' | 'chase' | 'flash';

/** Bulb tints: a dark bulb is warm brown, like the wood it sits in. */
const OFF = 0x86552a;
/** Brass a little aged, so the bezels sit in the wood rather than on it. */
const BRASS = 0xe6d2b0;
const LIT = 0xffc4e4;
const LIT_GLOW = 0xff5ab4;
const WIN = 0xffe9a0;
const WIN_GLOW = 0xffb93d;

/** The bezel art's hole is 71% of its width; the bulb tucks just under it. */
const BULB_OF_BEZEL = 0.74;

interface Lamp {
  /** A soft shadow round the socket, so it reads as sunk into the rail. */
  socket: Sprite;
  bulb: Sprite;
  bezel: Sprite;
  glow: Sprite;
}

/**
 * The fruit-machine lights down both sides of the reels, each in a brass
 * bezel set into the frame's side rail, so the reels keep their size. At
 * rest they show the Ready meter, filling from the bottom; the light being
 * filled warms as it fills, and the last two before a run blink.
 */
export class SideLights extends Container {
  private readonly left: Lamp[] = [];
  private readonly right: Lamp[] = [];
  private readonly count: number;
  private readonly reduced = prefersReducedMotion();
  private readonly bezelTexture: () => Texture | null;
  private placed: [number, number, number, number, number] | null = null;
  private mode: SideLightsMode = 'ready';
  private flashUntil = 0;
  private lit = 0;
  private next = 0;
  private clock = 0;

  /** `bezelTexture` is looked up again at each layout until it has loaded;
   *  the bulbs show bare until then. */
  constructor(count = 10, bezelTexture: () => Texture | null = () => null) {
    super();
    this.count = count;
    this.bezelTexture = bezelTexture;
    for (const side of [this.left, this.right]) {
      for (let i = 0; i < count; i++) {
        const socket = new Sprite(glowTexture());
        const bulb = new Sprite(bulbTexture());
        const bezel = new Sprite();
        const glow = new Sprite(glowTexture());
        for (const s of [socket, bulb, bezel, glow]) s.anchor.set(0.5);
        socket.tint = 0x000000;
        socket.alpha = 0.7;
        bezel.tint = BRASS;
        glow.blendMode = 'add';
        bezel.visible = false;
        this.addChild(socket, bulb, bezel, glow);
        side.push({ socket, bulb, bezel, glow });
      }
    }
  }

  /** Place the lights on the frame's left and right rails, bottom to top,
   *  clear of the gold blocks at the rails' ends (a ring-sized square at
   *  each corner of the frame). */
  layout(left: number, right: number, top: number, bottom: number, ring: number): void {
    this.placed = [left, right, top, bottom, ring];
    // The rail's carved strip is about 0.8 of the band, between a bevel on
    // each side; the bezel keeps a margin of wood clear of both.
    const size = Math.max(8, ring * 0.62);
    const pad = ring + size * 0.7;
    const span = bottom - top - pad * 2;
    const bezel = this.bezelTexture();
    for (const [side, x] of [[this.left, left + ring / 2], [this.right, right - ring / 2]] as const) {
      side.forEach((lamp, i) => {
        const y = bottom - pad - (span * i) / Math.max(1, this.count - 1);
        if (bezel) {
          lamp.bezel.texture = bezel;
          lamp.bezel.visible = true;
        }
        lamp.socket.width = lamp.socket.height = size * 1.3;
        lamp.bezel.width = lamp.bezel.height = size;
        lamp.bulb.width = lamp.bulb.height = size * (bezel ? BULB_OF_BEZEL : 0.8);
        lamp.glow.width = lamp.glow.height = size * 2.4;
        for (const s of [lamp.socket, lamp.bulb, lamp.bezel, lamp.glow]) {
          s.x = x;
          s.y = y;
        }
      });
    }
    this.paint();
  }

  /** Whole lights lit, and how far (0 to 1) the next one has filled. */
  setReady(lit: number, next: number): void {
    // The bezel art may have loaded since the last layout.
    if (this.placed && !this.left[0]?.bezel.visible && this.bezelTexture()) this.layout(...this.placed);
    this.lit = Math.max(0, Math.min(this.count, lit));
    this.next = Math.max(0, Math.min(1, next));
    this.paint();
  }

  setMode(mode: SideLightsMode, flashMs = 1200): void {
    this.mode = mode;
    if (mode === 'flash') this.flashUntil = this.clock + flashMs;
    this.paint();
  }

  tick(deltaMs: number): void {
    this.clock += deltaMs;
    if (this.mode === 'flash' && this.clock >= this.flashUntil) this.mode = 'ready';
    this.paint();
  }

  private paint(): void {
    const step = this.reduced ? REDUCED_STEP_MS : 70;
    const frame = Math.floor(this.clock / step);
    const win = this.mode === 'flash';
    for (const side of [this.left, this.right]) {
      side.forEach((lamp, i) => {
        const level = this.levelFor(i, frame);
        lamp.glow.alpha = level < 0.1 ? 0 : level;
        lamp.glow.tint = win ? WIN_GLOW : LIT_GLOW;
        lamp.bulb.tint = mix(OFF, win ? WIN : LIT, Math.min(1, level / 0.9));
      });
    }
  }

  /** How lit a light is, 0 (off) to about 1. */
  private levelFor(i: number, frame: number): number {
    if (this.mode === 'chase') {
      const head = frame % (this.count + 3);
      return i <= head && i > head - 3 ? 0.95 : 0;
    }
    if (this.mode === 'flash') {
      return this.reduced || frame % 2 === 0 ? 0.95 : 0;
    }
    if (i < this.lit) {
      const blink = this.lit >= this.count - 2 && !this.reduced && i === this.lit - 1;
      return blink && Math.floor(this.clock / 300) % 2 === 1 ? 0.3 : 0.9;
    }
    if (i === this.lit && this.next > 0) {
      const pulse = this.reduced ? 0 : 0.06 * Math.sin(this.clock / 180);
      return 0.1 + 0.35 * this.next + pulse;
    }
    return 0;
  }
}

/** Blend two colours, `t` from 0 (a) to 1 (b). */
function mix(a: number, b: number, t: number): number {
  const ch = (shift: number) => {
    const x = (a >> shift) & 0xff;
    const y = (b >> shift) & 0xff;
    return Math.round(x + (y - x) * t) << shift;
  };
  return ch(16) | ch(8) | ch(0);
}
